/**
 * CompactClient — calls the Morph /v1/compact endpoint.
 *
 * Returns per-message compacted_line_ranges showing which lines were removed.
 * HTTP goes through the shared `MorphAPIClient` transport.
 */
import { MorphAPIClient } from '../../core/client.js';
import { APIResource } from '../../core/resource.js';
import { MorphError } from '../utils/resilience.js';
import type { CompactConfig, CompactInput, CompactResult } from './types.js';

const DEFAULT_API_URL = 'https://api.morphllm.com';
const DEFAULT_TIMEOUT = 120000;

function resolveClient(clientOrConfig: MorphAPIClient | CompactConfig): MorphAPIClient {
  if (clientOrConfig instanceof MorphAPIClient) return clientOrConfig;
  return new MorphAPIClient({
    apiKey: clientOrConfig.morphApiKey,
    baseURL: clientOrConfig.morphApiUrl ?? DEFAULT_API_URL,
    timeout: clientOrConfig.timeout ?? DEFAULT_TIMEOUT,
    retryConfig: clientOrConfig.retryConfig,
    debug: clientOrConfig.debug,
  });
}

/**
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).compact`).
 * In edge runtimes where `MorphClient` is unavailable, pass a shared transport:
 * `new CompactClient(new MorphAPIClient({ apiKey }))`. Kept only for backwards compatibility.
 */
export class CompactClient extends APIResource {
  /** Resolved URL/timeout, exposed for backwards-compatible introspection. */
  readonly config: { morphApiUrl: string; timeout: number; debug: boolean };

  constructor(clientOrConfig: MorphAPIClient | CompactConfig = {}) {
    super(resolveClient(clientOrConfig));
    const cfg = clientOrConfig instanceof MorphAPIClient ? {} : clientOrConfig;
    this.config = {
      morphApiUrl: this._client.baseURL,
      timeout: cfg.timeout ?? DEFAULT_TIMEOUT,
      debug: cfg.debug ?? false,
    };
  }

  /**
   * Compact messages or text via /v1/compact.
   * Returns per-message `compacted_line_ranges` showing which lines were removed.
   *
   * @example
   * ```typescript
   * const client = new CompactClient({ morphApiKey: 'sk-...' });
   * const result = await client.compact({
   *   input: codeFile,
   *   query: "authentication",
   *   compressionRatio: 0.5,
   * });
   * console.log(result.output);
   * ```
   */
  async compact(input: CompactInput): Promise<CompactResult> {
    // Build request body with snake_case keys
    const body: Record<string, unknown> = {
      compression_ratio: input.compressionRatio ?? 0.5,
      preserve_recent: input.preserveRecent ?? 2,
      model: input.model ?? 'morph-compactor',
      include_line_ranges: input.includeLineRanges ?? true,
      include_markers: input.includeMarkers ?? true,
    };

    if (input.query !== undefined) body.query = input.query;

    // Normalize input: string → { input }, array → { messages }, messages field → { messages }
    if (input.messages) body.messages = input.messages;
    else if (typeof input.input === 'string') body.input = input.input;
    else if (Array.isArray(input.input)) body.messages = input.input;
    else throw new Error("Either 'input' or 'messages' must be provided");

    try {
      return await this._client.post<CompactResult>('/v1/compact', { body, timeout: this.config.timeout });
    } catch (err) {
      // Preserve the historical `Compact API error <status>: ...` message shape.
      if (err instanceof MorphError) {
        throw new Error(`Compact API error ${err.statusCode}: ${err.message}`);
      }
      throw err;
    }
  }
}
